<template>
  <div>
    <h1 class="title is-3">Reports</h1>

    <!-- Daily Report -->
    <div class="mb-5">
      <h2 class="title is-5 mb-2">Daily Dismissal Report</h2>
      <b-field label="Select date">
        <b-input type="date" v-model="selectedDate" @input="fetchDaily" />
      </b-field>

      <p class="mb-3" v-if="!loadingDaily">
        <b>{{ dailyRows.length }}</b> student{{ dailyRows.length !== 1 ? 's' : '' }} with records on {{ selectedDate }}
      </p>

      <b-table
        :data="dailyRows"
        :loading="loadingDaily"
        :mobile-cards="false"
        narrowed
        striped
        hoverable
      >
        <b-table-column field="name" label="Name" v-slot="props">{{ props.row.name }}</b-table-column>
        <b-table-column field="id" label="ID" numeric v-slot="props">{{ props.row.id }}</b-table-column>
        <b-table-column field="classroom" label="Classroom" v-slot="props">{{ props.row.classroom }}</b-table-column>
        <b-table-column field="station" label="Station" v-slot="props">{{ props.row.station || '—' }}</b-table-column>
        <b-table-column field="arrived" label="Arrived" v-slot="props">{{ formatTime(props.row.arrived) }}</b-table-column>
        <b-table-column field="student_in_transit" label="In Transit" v-slot="props">{{ formatTime(props.row.student_in_transit) }}</b-table-column>
        <b-table-column field="cleared" label="Cleared" v-slot="props">{{ formatTime(props.row.cleared) }}</b-table-column>
        <b-table-column label="History" v-slot="props">
          <button class="button is-small is-light" @click="openStudentHistory(props.row)">
            <span class="icon is-small"><i class="fas fa-clock"></i></span>
            <span>View History</span>
          </button>
        </b-table-column>

        <template #empty>
          <div class="has-text-centered py-4 has-text-grey">No records for this date.</div>
        </template>
      </b-table>
    </div>

    <!-- Student History Modal -->
    <b-modal v-model="studentModalDisplay" :width="500" scroll="keep">
      <div class="card" v-if="selectedStudent">
        <div class="card-content">
          <p class="title is-5">{{ selectedStudent.name }} (#{{ selectedStudent.id }})</p>
          <p class="subtitle is-6 mb-4">{{ selectedStudent.classroom }}</p>

          <div v-if="loadingStudentHistory" class="has-text-centered py-4">
            <b-loading :is-full-page="false" :active="true" />
          </div>
          <div v-else-if="studentHistoryRows.length === 0" class="has-text-grey has-text-centered py-4">
            No history found.
          </div>
          <div v-else>
            <div
              v-for="entry in studentHistoryRows"
              :key="entry.date"
              class="mb-3 p-3"
              style="border: 1px solid #dbdbdb; border-radius: 4px;"
            >
              <div class="is-flex is-justify-content-space-between is-align-items-center mb-1">
                <p class="has-text-weight-bold">{{ entry.date }}</p>
                <button class="button is-danger is-small is-light" @click="deleteStudentDayRecord(entry.date)">
                  <span class="icon is-small"><i class="fas fa-minus"></i></span>
                </button>
              </div>
              <div class="columns is-mobile is-multiline">
                <div class="column is-half">
                  <p class="heading mb-0">Caller Input</p>
                  <p>{{ formatTime(entry.arrived) }}</p>
                </div>
                <div class="column is-half">
                  <p class="heading mb-0">In Transit</p>
                  <p>{{ formatTime(entry.student_in_transit) }}</p>
                </div>
                <div class="column is-half">
                  <p class="heading mb-0">Cleared</p>
                  <p>{{ formatTime(entry.cleared) }}</p>
                </div>
                <div class="column is-half">
                  <p class="heading mb-0">Station</p>
                  <p>{{ entry.station || '—' }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-modal>

    <!-- Clear History (admin only) -->
    <div v-if="user.data && user.data.role === 'admin'" class="mt-6">
      <hr />
      <h2 class="title is-5">Danger Zone</h2>
      <button
        class="button is-danger"
        @click="
          clearModalDisplay = true;
          funnyWord = funnyWords[Math.floor(Math.random() * funnyWords.length)];
          clearVerificationText = '';
        "
      >
        Clear All History
      </button>

      <b-modal v-model="clearModalDisplay" :width="400" scroll="keep">
        <div class="card">
          <div class="card-content">
            <div class="content">
              <div class="title is-4">Clear all dismissal history?</div>
              <div class="subtitle">
                This permanently deletes all history for all dates. Confirm by typing
                <b>{{ funnyWord.toLowerCase() }}</b> below.
              </div>
              <b-field>
                <b-input type="text" v-model="clearVerificationText" />
              </b-field>
              <button
                class="button is-danger mt-2"
                :disabled="clearVerificationText !== funnyWord.toLowerCase()"
                @click="confirmClearHistory"
              >
                Yes do it!
              </button>
            </div>
          </div>
        </div>
      </b-modal>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { db } from '../db'

const school = window.location.pathname.split('/')[2]

export default {
  data() {
    return {
      selectedDate: new Date().toLocaleDateString('en-CA'),
      loadingDaily: false,
      dailyData: {},

      studentModalDisplay: false,
      selectedStudent: null,
      loadingStudentHistory: false,
      studentHistoryData: {},

      clearModalDisplay: false,
      clearVerificationText: '',
      funnyWord: 'Bumfuzzle',
      funnyWords: [
        'Bumfuzzle', 'Fartlek', 'Everywhen', 'Hullaballoo', 'Meldrop',
        'Obelus', 'Sozzled', 'Bumbershoot', 'Titter', 'Smicker',
        'Whippersnapper', 'Lollygag', 'Bamboozled', 'Flabbergast',
        'Cattywampus', 'Gobbledygook', 'Gardyloo',
      ],
    }
  },
  computed: {
    ...mapGetters({
      students: 'getStudents',
      user: 'user',
    }),
    dailyRows() {
      return Object.entries(this.dailyData).map(([id, record]) => {
        const student = this.students.find(s => s.id == id) || {}
        return {
          id,
          name: student.name || `Student #${id}`,
          classroom: student.classroom || '—',
          station: record.station,
          arrived: record.arrived,
          student_in_transit: record.student_in_transit,
          cleared: record.cleared,
        }
      }).sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    },
    studentHistoryRows() {
      return Object.entries(this.studentHistoryData)
        .map(([date, record]) => ({ date, ...record }))
        .sort((a, b) => b.date.localeCompare(a.date))
    },
  },
  methods: {
    formatTime(dateString) {
      if (!dateString) return '—'
      return new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },
    async fetchDaily() {
      this.loadingDaily = true
      this.dailyData = {}
      const snap = await db.ref(`schools/${school}/history/${this.selectedDate}`).once('value')
      this.dailyData = snap.val() || {}
      this.loadingDaily = false
    },
    async openStudentHistory(row) {
      this.selectedStudent = row
      this.studentHistoryData = {}
      this.studentModalDisplay = true
      this.loadingStudentHistory = true
      const snap = await db.ref(`schools/${school}/student_history/${row.id}`).once('value')
      this.studentHistoryData = snap.val() || {}
      this.loadingStudentHistory = false
    },
    async deleteStudentDayRecord(date) {
      const id = this.selectedStudent.id
      await Promise.all([
        db.ref(`schools/${school}/history/${date}/${id}`).remove(),
        db.ref(`schools/${school}/student_history/${id}/${date}`).remove(),
      ])
      this.$delete(this.studentHistoryData, date)
      if (date === this.selectedDate) {
        this.$delete(this.dailyData, id)
      }
    },
    confirmClearHistory() {
      this.$store.dispatch('clearHistory').then(() => {
        this.clearModalDisplay = false
        this.dailyData = {}
      })
    },
  },
  mounted() {
    this.fetchDaily()
  },
}
</script>
